import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Message, ReactionType, CalibrationReviewPayload } from '../types';
import type { ChipsPayload, ChipOption } from '../services/stevensV4Service';
import { ChatBubble, type ChatSurface } from './ChatBubble';
import { ThinkingIndicator } from './ThinkingIndicator';
import { CalibrationReviewCard } from './CalibrationReviewCard';
import { SuggestionChips } from './SuggestionChips';
import { RecallLine } from './RecallLine';
import { cn } from '@core/lib/utils';
import { chatConfig, useChatCopy, type ChatCopyPack } from '../../config';
import { MessageCircle, AlertCircle, ArrowDown } from 'lucide-react';
import { useStickyAutoscroll, type ScrollElementRef } from '../hooks/useStickyAutoscroll';

interface MessageListProps {
  messages: Message[];
  /**
   * TRF (#1731): host override per turn. Return a node to render INSTEAD of
   * the bubble (the turn-gap wrapper still applies); return undefined for
   * the bubble as usual. Host-only turns — handoff events, staff bubbles —
   * render through this, so nothing audience-voiced enters the core.
   */
  renderTurn?: (message: Message, index: number) => React.ReactNode | undefined;
  isTyping?: boolean;
  typingSender?: 'stevens' | 'claude' | 'cristina';
  error?: string | null;
  className?: string;
  coachName?: string;
  onRetryMessage?: (messageId: string) => void;
  onReactToMessage?: (messageId: string, reactionType: ReactionType) => void;
  /** #94 client-flag gesture: flag an AI message as an insight candidate. */
  onFlagMessage?: (messageId: string, content: string) => Promise<void> | void;
  pendingCalibrationReviews?: CalibrationReviewPayload[];
  onCalibrationApprove?: (entryId: string) => void;
  onCalibrationHold?: (entryId: string) => void;
  onCalibrationDelete?: (entryId: string) => void;
  onCalibrationEdit?: (entryId: string, field: 'headline' | 'first_impression', value: string) => void;
  /**
   * #269: custom export-range selection. When true, rows become
   * click-to-mark targets; the parent owns the anchor state and passes back
   * the resolved inclusive index range to highlight.
   */
  selectionMode?: boolean;
  selectedFromIndex?: number | null;
  selectedToIndex?: number | null;
  onSelectMessage?: (messageId: string, index: number) => void;
  /**
   * #672/#673: the element that actually owns the scroll. On pages where an
   * ancestor is the scrollport (chat.tsx, CristinaChat.tsx) this component’s
   * own root never scrolls, so autoscroll must observe the real container.
   * Omit it and the component binds to itself, which is correct for
   * WorkspaceChat and NotesWorkspacePage.
   */
  scrollContainerRef?: ScrollElementRef;
  /** Conversation id — switching conversations clears the autoscroll latch. */
  conversationId?: string;
  /**
   * #673 (spec B3, "reset the latch on user send"): bump this on every send.
   * Any change re-anchors the log to the newest content, so a user who
   * scrolled up still sees their own message and the reply that follows.
   */
  resetSignal?: number;
  /** #681: rendering model for the bubbles. See ChatSurface. */
  surface?: ChatSurface;
  /**
   * #1308 (CP-21): standing suggestion-chip offering for the next user turn
   * (from useStevensV4.chips). Rendered under the latest agent turn; hidden
   * while a reply is streaming (the offering is consumed by the next send).
   */
  chips?: ChipsPayload | null;
  onChipSelect?: (option: ChipOption) => void;
}

const EmptyState = React.memo(function EmptyState() {
  const copy = useChatCopy().messageList;
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8" role="status">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
        <MessageCircle className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
      </div>
      <h3 className="text-card-title font-medium text-foreground mb-2">
        {copy.emptyTitle}
      </h3>
      <p className="text-body-sm text-muted-foreground max-w-sm">
        {copy.emptyBody}
      </p>
    </div>
  );
});

const ErrorDisplay = React.memo(function ErrorDisplay({ error }: { error: string }) {
  return (
    <div 
      className="flex items-center gap-2 p-3 mb-4 bg-destructive/10 border border-destructive/20 rounded-lg"
      role="alert"
      aria-live="polite"
    >
      <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" aria-hidden="true" />
      <span className="text-body-sm text-destructive">{error}</span>
    </div>
  );
});

/**
 * Chat UX brief 2026-09-07 (change #2): the transcript's clock.
 *
 * Per-message stamps are gone from the prose bubble, so the date is announced
 * once, above the first turn of each calendar day. These two helpers answer
 * "which day is this?" and "what do we call it?" separately, because only the
 * first is allowed to be timezone-sensitive.
 *
 * `localDayKey` compares LOCAL calendar components, never UTC and never a
 * millisecond span: two messages 40 minutes apart across local midnight are
 * different days, and two messages 20 hours apart inside one long DST day are
 * not. The returned string doubles as the `<time dateTime>` value, so it is
 * shaped YYYY-MM-DD. Unparseable timestamps return null — the caller then
 * renders no divider rather than an "Invalid Date" label.
 */
function localDayKey(timestamp: Date | number): string | null {
  const d = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
  if (Number.isNaN(d.getTime())) return null;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * "Aug 18" inside the current year, "Aug 18, 2025" outside it — the year is
 * noise until it is the one thing that disambiguates. The month name comes
 * from Intl (the copy pack's `time.date` slot, #3168), so this is a FORMATTED DATE and not brand copy:
 * it needs no ChatCopyPack slot (Guard B) and it reads correctly in every
 * locale. Deliberately no "Today"/"Yesterday" — those are words, they are
 * brand voice, and they go stale in a tab left open past midnight.
 */
function dayDividerLabel(timestamp: Date | number, formatDay: ChatCopyPack['time']['date'], now: Date = new Date()): string {
  const d = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
  if (Number.isNaN(d.getTime())) return '';
  // #2109 R3-B24 + #3168: the copy pack's Intl slot, so the month name follows the UI.
  return formatDay(d, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}


export const MessageList = React.memo(function MessageList({
  messages,
  renderTurn,
  isTyping = false,
  typingSender = 'stevens',
  error,
  className,
  coachName,
  onRetryMessage,
  onReactToMessage,
  onFlagMessage,
  pendingCalibrationReviews = [],
  onCalibrationApprove,
  onCalibrationHold,
  onCalibrationDelete,
  onCalibrationEdit,
  selectionMode = false,
  selectedFromIndex = null,
  selectedToIndex = null,
  onSelectMessage,
  scrollContainerRef,
  conversationId,
  resetSignal,
  surface = 'bubble',
  chips = null,
  onChipSelect,
}: MessageListProps) {
  // #2109 R3-B24: subscribing re-renders this memoised list on a language switch.
  const copy = useChatCopy();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // #681/#683: every visual-grammar branch in this file hangs off this flag.
  // Only pages/chat.tsx passes `prose`; the other three consumers must render
  // exactly as they did pre-wave (spec Scope table, D6).
  const isProse = surface === 'prose';

  // #281: Render-time chronological sort, independent of whatever order the
  // `messages` array arrives in. History rehydration (page reload) sources
  // its order from a backend/Neo4j response, and streaming/optimistic
  // updates append client-side — rather than guard every producer against
  // every possible race, sort once here by the authoritative `timestamp`
  // field so the rendered order is always correct. Array.prototype.sort is
  // stable, so messages with equal timestamps keep their relative (already
  // backend-sorted) order.
  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [messages]);

  // #1308: true while any reply is streaming in — the chips gate needs this
  // because `isTyping` only covers the pre-first-token window on LMC.
  const streamActive = useMemo(
    () => sortedMessages.some((m) => m.status === 'sending' || m.isStreaming),
    [sortedMessages]
  );

  // #1308 (C-1, arch doc §8b): the viewport anchors at the TOP of an incoming
  // message and never force-follows the streaming tail. Depending on
  // `sortedMessages.length` (not the array) scrolls exactly once, when a new
  // turn ENTERS the log — the incoming message is empty at that moment, so
  // end-anchoring then IS top-of-message anchoring — and never again as its
  // content grows.
  const { isLatched, jumpToLatest } = useStickyAutoscroll({
    containerRef: scrollContainerRef ?? containerRef,
    endRef: messagesEndRef,
    deps: [sortedMessages.length, isTyping],
    resetKey: conversationId,
    resetSignal,
  });

  // #1308 (C-1): the jump-to-latest affordance must also appear when a
  // streaming reply has GROWN past the fold â the user hasn’t scrolled, but
  // the latest content is off screen all the same. jsdom has no
  // IntersectionObserver; endVisible then stays true and the pill keeps its
  // pre-#1308 latch-only trigger.
  const [endVisible, setEndVisible] = useState(true);

  /**
   * #673: where the jump-to-latest pill has to live.
   *
   * `position: sticky` resolves against the nearest scrollport ancestor, and
   * this root always creates one (`overflow-y-auto`) even on the pages where
   * it has no scroll range. So a pill rendered inside this root sticks to an
   * element that never scrolls: it stays in normal flow at the very bottom of
   * the thread, below the fold, precisely when the user has scrolled up. It
   * must be placed in the element that really scrolls — which, when the
   * caller names one, is not this root.
   *
   * Resolved in an effect because refs are only populated after commit. The
   * ancestor’s ref is attached in the same commit as this component’s own, so
   * it is readable by the time effects run.
   */
  const [externalScrollHost, setExternalScrollHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const host = scrollContainerRef?.current ?? null;
    setExternalScrollHost(host && host !== containerRef.current ? host : null);
  }, [scrollContainerRef]);

  // #1308 (C-1): observe the end sentinel so the jump-to-latest affordance
  // also appears when a streaming reply has GROWN past the fold. Keyed on
  // `externalScrollHost` (STATE, resolved after commit) rather than the ref
  // object â a ref’s identity never changes, so a ref-keyed effect would
  // observe the wrong scrollport when the external host mounts after this
  // component (review finding, 2026-08-31). jsdom has no
  // IntersectionObserver; endVisible then stays true and the pill keeps its
  // latch-only trigger.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const end = messagesEndRef.current;
    if (!end) return;
    const observer = new IntersectionObserver(
      (entries) => setEndVisible(entries[0]?.isIntersecting ?? true),
      { root: externalScrollHost ?? containerRef.current, threshold: 0 }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [externalScrollHost]);

  const jumpPill = (isLatched || !endVisible) ? (
    <div
      className="pointer-events-none sticky bottom-4 z-20 flex justify-center"
      // The pill is chrome, not log content. When this root IS the scrollport
      // the pill sits inside the role="log" aria-live region, so opt its
      // subtree out explicitly or screen readers announce "Jump to latest"
      // as if it were a new message.
      aria-live="off"
    >
      <button
        type="button"
        onClick={jumpToLatest}
        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-chat-border bg-chat-surface px-3 py-1.5 text-[length:calc(12rem/17*var(--content-font-scale))] text-chat-text shadow-chat-md transition-colors hover:bg-chat-surface-hover"
      >
        <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
        {copy.messageList.jumpToLatest}
      </button>
    </div>
  ) : null;

  return (
    <div 
      ref={containerRef}
      className={cn(
        'flex-1 overflow-y-auto',
        // #683 (G6): on the prose surface the horizontal padding belongs to
        // the reading rail in chat.tsx, not to the log, and the last message
        // sits the log's own py-2 off the pinned composer. Gated on
        // `surface` because the three bubble consumers — CristinaChat,
        // WorkspaceChat, NotesWorkspacePage — must render byte-identically,
        // and they own no rail of their own to put the 16px inset back.
        isProse ? 'px-0 py-2' : 'p-4',
        className
      )}
      data-surface={surface}
      role="log"
      aria-label={copy.messageList.regionLabel}
      aria-live="polite"
      aria-atomic="false"
    >
      {error && <ErrorDisplay error={error} />}

      {sortedMessages.length === 0 ? (
        <EmptyState />
      ) : (
        // #681: the per-turn rhythm replaces the flat `space-y-4` — on the
        // prose surface only. The three bubble consumers keep the 16px
        // uniform stack they shipped with (spec Scope table, D6).
        <div className={isProse ? undefined : 'space-y-4'}>
          {/* Split completed and streaming messages for optimal memoization */}
          {sortedMessages.map((message, index) => {
            const isStreamingMessage = message.status === 'sending';
            const isLastMessage = index === sortedMessages.length - 1;

            // #681: a question and its answer are one unit (32px); a new
            // question opens a real break (56px). Ratio ~1.75:1. A flat gap
            // makes every boundary cost the same, so none of them mean
            // anything. Prose surface only — `undefined` here means the
            // bubble path emits neither the attribute nor the style, so its
            // DOM is unchanged from pre-wave.
            //
            // NOTE the Message type field is `sender`, NOT `role` — the
            // spatial-grammar spec §3 says `role`, which does not exist here.
            const prev = index > 0 ? sortedMessages[index - 1] : null;
            const gapKind = !isProse
              ? undefined
              : !prev
                ? 'none'
                : prev.sender === message.sender
                  ? 'same'
                  : message.sender === 'user'
                    ? 'turn'
                    : 'answer';

            // Chat UX brief 2026-09-07 (change #2): the day divider. It is
            // emitted before the FIRST turn of every calendar day, the very
            // first day included — the brief is explicit that this is not a
            // "when the day changes" rule, so there is no `prev &&` guard on
            // the condition itself. `prev` only decides how much air opens
            // ABOVE the label: a full turn break mid-transcript, none at the
            // top where there is nothing to break from.
            //
            // A message whose timestamp will not parse gets no divider rather
            // than an "Invalid Date" label; the turn itself still renders.
            const dayKey = isProse ? localDayKey(message.timestamp) : null;
            const prevDayKey = isProse && prev ? localDayKey(prev.timestamp) : null;
            const dayDivider =
              dayKey !== null && dayKey !== prevDayKey ? (
                <div
                  className="flex justify-center"
                  data-date-divider={dayKey}
                  style={{
                    marginTop: prev ? 'var(--chat-gap-turn)' : undefined,
                    marginBottom: 'var(--chat-gap-answer)',
                  }}
                >
                  <time
                    className="text-caption"
                    dateTime={dayKey}
                    style={{ color: 'var(--stevens-ink-2)', fontSize: 'var(--chat-meta-size)' }}
                    suppressHydrationWarning={true}
                  >
                    {dayDividerLabel(message.timestamp, copy.time.date)}
                  </time>
                </div>
              ) : null;

            // TRF delta 2026-09-07 (adversarial review, finding 4): who gets
            // a speaker name back. Two conditions, both narrow.
            //
            // VOICE ONLY. A typed thread is attributed by shape — the client's
            // turn is a tinted right-aligned box, the agent's is left-aligned
            // markdown — so the brief was right to delete the label row there.
            // A call transcript has neither cue: both sides are plain speech
            // and the tint between them is one paper step, so the column reads
            // as one voice.
            //
            // FIRST TURN OF A RUN ONLY. `gapKind === 'same'` is already this
            // component's definition of "the previous turn had this speaker",
            // so reusing it means the label and the 8px continuation gap can
            // never disagree about where a run begins.
            const speakerMark =
              isProse &&
              message.metadata?.source === 'voice' &&
              gapKind !== 'same';

            // The divider already carries the break between the two days, so
            // the turn under it drops its own top gap — otherwise the label
            // sits jammed against the message it introduces while a second
            // 32-56px sits above it.
            const gapStyle =
              gapKind && !dayDivider
                ? {
                    none: undefined,
                    same: 'var(--chat-gap-same)',
                    turn: 'var(--chat-gap-turn)',
                    answer: 'var(--chat-gap-answer)',
                  }[gapKind]
                : undefined;

            // #1305: retry is offered only where the backend can honor it —
            // the LAST message, an assistant reply, carrying an authoritative
            // "{conv}-asst-{turn}" id (client-UUID bubbles, incl. error
            // bubbles, were never persisted and have no server-side target).
            // #1308 (CP-29/§32.1): and never while a reply is streaming in.
            const canRetry =
              Boolean(onRetryMessage) &&
              isLastMessage &&
              message.sender !== 'user' &&
              /-asst-\d+$/.test(message.id) &&
              !isStreamingMessage &&
              !message.isStreaming &&
              !isTyping;

            const bubble = (
              <>
                {/* #1308 (CP-20/§14.7): quiet recall line — a system line,
                    not a speech turn — above the reply it shaped. */}
                {message.sender !== 'user' && message.memoryRecall && (
                  <RecallLine recall={message.memoryRecall} />
                )}
                <ChatBubble
                  message={message}
                  isNew={isLastMessage && !isStreamingMessage}
                  coachName={coachName}
                  onRetry={
                    canRetry && onRetryMessage
                      ? () => onRetryMessage(message.id)
                      : undefined
                  }
                  onReact={onReactToMessage}
                  onFlag={onFlagMessage}

                  messageIndex={index}
                  surface={surface}
                  speakerMark={speakerMark}
                />
                {/* WAVE27-A #195: living framework artifact cards attached to
                    this message (streaming turn or Tier-3 transcript replay).
                    Kind-gated so unknown persisted kinds render nothing.
                    #1302 (CP-25 richCards 'registered'): brand-owned cards —
                    LMC registers EitcMapCard under 'eitc_map' — are looked up
                    from BrandChatConfig.cards by rich-block kind.
                    #1308 (CP-19/C-9): a brand with richCards 'off' renders no
                    cards at all — a card without brand wording is a defect,
                    not a fallback (grammar §33.1). */}
                {chatConfig().capabilities.richCards !== 'off' &&
                  Array.from(new Set(message.richBlocks ?? [])).map((kind) => {
                    const RichCard = chatConfig().cards[kind];
                    return RichCard ? <RichCard key={kind} /> : null;
                  })}
              </>
            );

            // TRF (#1731): a host-rendered turn replaces the bubble, keeps the gap.
            const turn = renderTurn?.(message, index) ?? bubble;

            // #269: click-to-mark wrapper. The parent resolves anchors to an
            // inclusive index range; NOTE this assumes `messages` arrives
            // already chronologically sorted (both current producers sort in
            // useStevensV4), so the stable re-sort above preserves indices.
            const isSelected =
              selectionMode &&
              selectedFromIndex !== null &&
              selectedToIndex !== null &&
              index >= selectedFromIndex &&
              index <= selectedToIndex;

            // Bubble keeps the bare Fragment it had pre-wave; the extra
            // wrapper exists only to hang the prose turn gap on.
            const row = selectionMode ? (
              <div
                key={message.id}
                data-turn-gap={gapKind}
                style={isProse ? { marginTop: gapStyle } : undefined}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={copy.messageList.selectForRange(index + 1)}
                data-testid={`message-select-${index}`}
                onClick={(e) => {
                  // Ignore clicks on the bubble’s own controls (copy / retry /
                  // flag / thinking-toggle / reactions) — they bubble up here
                  // and would otherwise mutate the marked range.
                  if ((e.target as HTMLElement).closest('button, a, [role="menuitem"]')) return;
                  onSelectMessage?.(message.id, index);
                }}
                onKeyDown={(e) => {
                  if ((e.target as HTMLElement).closest('button, a, [role="menuitem"]')) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectMessage?.(message.id, index);
                  }
                }}
                className={cn(
                  '-mx-2 cursor-pointer rounded-lg px-2 py-1 transition-colors',
                  isSelected
                    ? 'bg-chat-accent/10 ring-1 ring-chat-accent/60'
                    : 'hover:bg-chat-surface-hover/60',
                )}
              >
                {turn}
              </div>
            ) : isProse ? (
              <div key={message.id} data-turn-gap={gapKind} style={{ marginTop: gapStyle }}>
                {turn}
              </div>
            ) : (
              <React.Fragment key={message.id}>{turn}</React.Fragment>
            );

            // The divider is a sibling of the turn, never a child of it: it
            // belongs to the boundary between two days, not to the message
            // that happens to open the later one. It therefore also precedes
            // a host-rendered turn (`renderTurn`) unchanged.
            return dayDivider ? (
              <React.Fragment key={`day-${message.id}`}>
                {dayDivider}
                {row}
              </React.Fragment>
            ) : (
              row
            );
          })}

          {isTyping && (
            <ThinkingIndicator sender={typingSender} coachName={coachName} />
          )}

          {/* #1308 (CP-21/§14.5): the standing chip offering, under the
              latest agent turn. Hidden while a reply streams — `isTyping`
              alone is NOT that guard (LMC passes isLoading && !isStreaming,
              which flips false at the first token), so the gate also checks
              the messages themselves; a tap during a stream would be
              silently dropped by the hook’s isLoading guard (review finding,
              2026-08-31). A fresh ui_chips event re-populates after. */}
          {chips && !isTyping && !streamActive && onChipSelect && (
            <SuggestionChips chips={chips} onSelect={onChipSelect} className="mt-3" />
          )}

          {pendingCalibrationReviews.map(entry => (
            <CalibrationReviewCard
              key={entry.entry_id}
              entry={entry}
              onApprove={() => onCalibrationApprove?.(entry.entry_id)}
              onHold={() => onCalibrationHold?.(entry.entry_id)}
              onDelete={() => onCalibrationDelete?.(entry.entry_id)}
              onEdit={(field, value) => onCalibrationEdit?.(entry.entry_id, field, value)}
            />
          ))}
        </div>
      )}
      
      {/* #673 jump-to-latest. `sticky bottom-4` only ever offsets inside the
          element that actually scrolls, so when an ancestor owns the scroll
          the pill is PORTALED into it — rendering it here would leave it in
          normal flow at the bottom of a thread the user has scrolled up from,
          i.e. off screen exactly when it is needed. The React position is the
          same either way; only the DOM parent moves. */}
      {externalScrollHost === null ? jumpPill : createPortal(jumpPill, externalScrollHost)}

      {/* Invisible element to scroll to */}
      <div ref={messagesEndRef} aria-hidden="true" />
    </div>
  );
});